import { IInterviewDetails, useGetInterviewDetails } from '@/core/protected/Candidate/query';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

export { BOT_STATUS, type RawBotStatus } from '@/constants/botStatus';
import { BOT_STATUS, RawBotStatus } from '@/constants/botStatus';

export const BotStatusEnum: Record<RawBotStatus, string> = {
  JOINING_CALL: 'CONNECTING',
  IN_WAITING_ROOM: 'WAITING TO BE ADMITTED',
  IN_CALL_NOT_RECORDING: 'PAUSED',
  IN_CALL_RECORDING: 'CONNECTED',
  CALL_ENDED: 'DISCONNECTED',
};

export type BotStatusType = (typeof BotStatusEnum)[keyof typeof BotStatusEnum];

export const isBotPending = (status: string | null | undefined) =>
  status === BOT_STATUS.JOINING_CALL || status === BOT_STATUS.IN_WAITING_ROOM;
export interface BaseChatItem<T extends string = string> {
  message_type: T;
  user_name: string;
  message_text: string;
}
export interface SelectIntervieweeData extends BaseChatItem<'select_interviewee'> {
  participants: string[];
}

export interface GenericChatItem
  extends BaseChatItem<'message' | 'transcription' | 'interview-end' | 'ending-phase'> {}
export interface FollowUpChatItem extends BaseChatItem<'follow-up'> {
  question_index: number | string;
  follow_up_question: string;
  answer_context: string;
}
export interface NewQuestion extends BaseChatItem<'new-question'> {
  question_index: number | string;
  is_follow_up: boolean;
}

export interface Evaluation extends BaseChatItem<'evaluation'> {
  question_index: number | string;
  evaluation: {
    rating: number;
    rubric: string;
    reasoning: string;
    what_was_answered: string;
    what_was_missed: string;
  };
}

export interface BotStatus extends BaseChatItem<'bot_status'> {
  message_text: BotStatusType;
}

export interface QuestionRefetchMessage extends BaseChatItem<'question-refresh'> {
  question_index: number | string;
  question_rephrased: {
    rephrased_question: string;
    rephrase_reason: string;
    original_question: string;
    ideal_answer: string;
    time: number;
  };
  is_follow_up: boolean;
}

export interface EnableEvaluation extends BaseChatItem<'enable-evaluation'> {
  question_index: number | string;
  is_follow_up: boolean;
}

/**
 * One of the campaign's standard questions, as the agent asks it.
 *
 * `message_text` carries the question itself, so these arrive whether or not
 * the interview details happened to include the list.
 */
export interface StandardQuestionAsked extends BaseChatItem<'standard-question-asked'> {
  question_index: number | string;
}

/**
 * Which question the recruiter is on, whichever list it came from.
 *
 * Standard and generated questions interleave — a standard question can be
 * asked long after the generated ones have started — so "current" is simply the
 * most recent asking of either kind, which only the chat stream's own order can
 * say.
 */
export interface AskedQuestionRef {
  source: 'standard' | 'generated';
  question_index: number;
}

export type ChatMessage =
  | GenericChatItem
  | NewQuestion
  | FollowUpChatItem
  | SelectIntervieweeData
  | Evaluation
  | BotStatus
  | QuestionRefetchMessage
  | EnableEvaluation
  | StandardQuestionAsked;

interface InterviewContextProps {
  chats: ChatMessage[];
  setChats: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  interviewQuestions: any[];
  setInterviewQuestions: React.Dispatch<React.SetStateAction<any[]>>;
  participants: string[];
  showTranscript: boolean;
  setParticipants: React.Dispatch<React.SetStateAction<string[]>>;
  setShowTranscript: React.Dispatch<React.SetStateAction<boolean>>;
  intervieweeName: string | null;
  setIntervieweeName: React.Dispatch<React.SetStateAction<string | null>>;
  // processed chats
  followUpQuestions: FollowUpChatItem[];
  newQuestions: NewQuestion[];
  evaluations: Evaluation[];
  messages: ChatMessage[];
  transcriptionData: GenericChatItem[];
  selectIntervieweeData: SelectIntervieweeData[];
  questionRefetches: QuestionRefetchMessage[];
  enableEvaluations: EnableEvaluation[];
  standardQuestionsAsked: StandardQuestionAsked[];
  lastAskedQuestion: AskedQuestionRef | null;
  interviewEnded: boolean;
  /** The agent is wrapping up — it has stopped taking answers, but the call is
   *  not over and `interviewEnded` is still false. */
  endingPhase: boolean;
  botStatus: BotStatusType | null;
  setBotStatus: React.Dispatch<React.SetStateAction<BotStatusType | null>>;
  botPending: boolean;
  interviewDetail: IInterviewDetails | undefined;
  interviewDetailLoading: boolean;
  // True while the user is deliberately ending the interview, so the leave guards
  // do not challenge the navigation the Stop button performs itself. A ref, not
  // state, because that navigation happens before the next render.
  stoppingRef: React.MutableRefObject<boolean>;
  setInterviewEnded: React.Dispatch<React.SetStateAction<boolean>>;
}

export const InterviewContext = createContext<InterviewContextProps | undefined>(undefined);

export const InterviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showTranscript, setShowTranscript] = useState(false);

  const [chats, setChats] = useState<ChatMessage[]>([]);
  const [interviewQuestions, setInterviewQuestions] = useState<any[]>([]);
  const [participants, setParticipants] = useState<string[]>([]);
  const [intervieweeName, setIntervieweeName] = useState<string | null>(null);
  const [initialBotStatus, setInitialBotStatus] = useState<BotStatusType | null>(null);
  const stoppingRef = useRef(false);
  const [interviewEnded, setInterviewEnded] = useState<boolean>(false);

  const { parsedResumeId } = useParams<{
    parsedResumeId: string;
  }>();

  const { data: interviewDetail, isLoading } = useGetInterviewDetails(parsedResumeId!!);

  const processedChats = useMemo(() => {
    const followUpQuestions: FollowUpChatItem[] = [];
    const newQuestions: NewQuestion[] = [];
    const evaluations: Evaluation[] = [];
    const messages: ChatMessage[] = [];
    const transcriptionData: GenericChatItem[] = [];
    const selectIntervieweeData: SelectIntervieweeData[] = [];
    const questionRefetches: QuestionRefetchMessage[] = [];
    const enableEvaluations: EnableEvaluation[] = [];
    const standardQuestionsAsked: StandardQuestionAsked[] = [];
    let lastAskedQuestion: AskedQuestionRef | null = null;
    /**
     * The agent has started wrapping the interview up. Derived straight from the
     * messages rather than held in state: it is a pure function of what has
     * arrived, so it cannot fall behind them the way a separate flag can.
     */
    let endingPhase = false;
    // let interviewEnded = false;

    for (const chat of chats) {
      switch (chat.message_type) {
        case 'follow-up':
          followUpQuestions.push(chat);
          messages.push(chat);
          break;
        case 'new-question':
          newQuestions.push(chat);
          lastAskedQuestion = { source: 'generated', question_index: Number(chat.question_index) };
          break;
        case 'evaluation':
          evaluations.push(chat);
          messages.push(chat);
          break;
        case 'transcription':
          transcriptionData.push(chat);
          break;
        case 'select_interviewee':
          selectIntervieweeData.push(chat);
          break;
        case 'message':
          messages.push(chat);
          break;
        case 'interview-end':
          setInterviewEnded(true);
          break;
        case 'ending-phase':
          endingPhase = true;
          break;
        case 'question-refresh':
          questionRefetches.push(chat);
          break;
        case 'enable-evaluation':
          enableEvaluations.push(chat);
          break;
        case 'standard-question-asked':
          standardQuestionsAsked.push(chat);
          lastAskedQuestion = { source: 'standard', question_index: Number(chat.question_index) };
          break;
      }
    }

    return {
      followUpQuestions,
      newQuestions,
      evaluations,
      messages,
      transcriptionData,
      selectIntervieweeData,
      questionRefetches,
      enableEvaluations,
      standardQuestionsAsked,
      lastAskedQuestion,
      endingPhase,
      setInterviewEnded,
    };
  }, [chats]);

  useEffect(() => {
    if (interviewDetail) {
      setChats(interviewDetail.interview_logs || []);
      setIntervieweeName(interviewDetail.interviewee_name || null);
      setInitialBotStatus((interviewDetail.bot_status as BotStatusType) ?? null);
    }
  }, [interviewDetail, parsedResumeId]);

  const contextValue = {
    chats,
    setChats,
    interviewQuestions,
    setInterviewQuestions,
    participants,
    setParticipants,
    showTranscript,
    setShowTranscript,
    intervieweeName,
    setIntervieweeName,
    interviewDetail,
    interviewDetailLoading: isLoading,
    stoppingRef,
    ...processedChats,
    // Read from state, not from the memo above. The memo recomputes only when
    // `chats` changes, so the copy it closed over was the value from before
    // `interview-end` was seen — leaving every consumer told the interview was
    // still running until some later message happened to arrive, and told so
    // indefinitely when the Stop button ended it, since that writes no message
    // at all.
    interviewEnded,
    botStatus: initialBotStatus,
    setBotStatus: setInitialBotStatus,
    botPending: isBotPending(initialBotStatus),
  };

  return <InterviewContext.Provider value={contextValue}>{children}</InterviewContext.Provider>;
};

export const useInterviewContext = () => {
  const context = useContext(InterviewContext);

  if (!context) {
    throw new Error('useInterviewContext must be used within InterviewProvider');
  }

  return context;
};
